import React, { useCallback, useEffect, useMemo, forwardRef } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import ResolveMultiSelect from 'components/ResolveMultiSelect';
import { ResolveMultiSelectRef } from 'components/ResolveMultiSelect/types';
import useDebounceSearch from 'hooks/useDebounceSearch';
import useMatchMedia from 'hooks/useMatchMedia';
import { Option } from 'models/Global';
import { getHasElementsInArray, getJoinedWordsWithComma } from 'utils';
import { EMPTY_FILTER_VALUE, SearchableFilterValue } from 'models/Filters';
import useSearchableFilters from 'hooks/useSearchableFilters';

import './SearchableMultiSelectFilter.styles.scss';

type SearchableMultiSelectFilterProps = {
  className?: string;
  value?: SearchableFilterValue;
  filterKey?: string;
  triggerTitle?: string;
  pillText?: string;
  hasCheckboxAll?: boolean;
  initialOptions?: Option[];
  hasEmptyOption?: boolean;
  emptyOptionLabel?: string;
  onSelect: (value: SearchableFilterValue) => void;
  getOptionsOnSearch: (searchString: string) => Promise<Option[]>;
  onApply?: () => void;
};

const SearchableMultiSelectFilter: React.ForwardRefRenderFunction<
  ResolveMultiSelectRef,
  SearchableMultiSelectFilterProps
> = (props, ref) => {
  const {
    className,
    value,
    filterKey,
    triggerTitle,
    pillText,
    hasCheckboxAll = false,
    initialOptions,
    hasEmptyOption = false,
    emptyOptionLabel,
    onSelect,
    getOptionsOnSearch,
    onApply,
  } = props;

  const { isLoading, hasError, options, onSearch, resetState } =
    useSearchableFilters(getOptionsOnSearch, initialOptions);

  const { t } = useTranslation();
  const isPhablet = useMatchMedia('isPhablet');

  const classes = classNames('searchable-multi-select-filter', className);

  const selected = useMemo(
    () => (Array.isArray(value) ? value : value ? [value] : []),
    [value],
  );

  const placeholder = useMemo(
    () =>
      triggerTitle ||
      (getHasElementsInArray(selected)
        ? getJoinedWordsWithComma(selected.map(({ label }) => label))
        : t('selectAnOption')),
    [triggerTitle, selected, t],
  );

  const mergedOptions = useMemo(() => {
    const selectedIds = new Set(selected.map(({ value }) => value));

    const selectedButNotInOptions = selected.filter(
      (selected) => !options.some(({ value }) => selected.value === value),
    );

    const pinnedSelected = options.filter(({ value }) =>
      selectedIds.has(value),
    );
    const unselected = options.filter(({ value }) => !selectedIds.has(value));

    const merged = [
      ...selectedButNotInOptions,
      ...pinnedSelected,
      ...unselected,
    ];

    if (!hasEmptyOption) return merged;

    return [
      ...merged.filter(({ value }) => value !== EMPTY_FILTER_VALUE),
      { value: EMPTY_FILTER_VALUE, label: emptyOptionLabel || t('isEmpty') },
    ];
  }, [options, selected, hasEmptyOption, emptyOptionLabel, t]);

  const { isDebouncing, onSearchInputChange, resetSearchString } =
    useDebounceSearch({ onSearch });

  useEffect(() => {
    resetState();
    resetSearchString();
  }, [filterKey, resetSearchString, resetState]);

  const handleChange = useCallback(
    (selectedIds: string[]) =>
      onSelect(
        selectedIds.map((id) => ({
          value: id,
          label: mergedOptions.find(({ value }) => value === id)?.label,
        })),
      ),
    [mergedOptions, onSelect],
  );

  return (
    <ResolveMultiSelect
      ref={ref}
      key={filterKey}
      className={classes}
      pillText={pillText}
      searchable
      hasCheckbox
      hasCheckboxAll={hasCheckboxAll}
      value={selected.map(({ value }) => value)}
      options={mergedOptions}
      loading={isLoading || isDebouncing}
      renderAsPortal={!isPhablet}
      placeholder={placeholder}
      searchPlaceholder={t('typeToSearch')}
      noDataTitle={t('typeToViewAvailableOptions')}
      emptySearchTitle={t('noMatchesFound')}
      errorTitle={hasError ? t('searchErrorTryAgain') : undefined}
      onSearchChange={onSearchInputChange}
      onChange={handleChange}
      onApply={onApply}
    />
  );
};

export default forwardRef(SearchableMultiSelectFilter);
